<template>
  <div
    :class="{
      'centered-qr-template-pass': isValidPassAndCenteredQR,
      'standard-template-pass': isStandard,
    }"
  >
    <ThePassIssuedExternalCouponNotification
      ref="ThePassIssuedExternalCouponNotification"
      :retry="retryIssuedExternalCoupon"
      :open-coupon-version-warning="openCouponVersionWarning"
      :is-not-target-version="isNotTargetVersion"
    />
    <div v-if="isValidPassAndCenteredQR" class="centered-qr-header-section">
      <TheHeader
        :header-title="'利用中のチケット'"
        :click-back-button-event="clickBackButtonEvent"
        :ticket-name="displayPassName(bookGroup, masterPass)"
        class="centered-qr-header"
        text-align="left"
      />
    </div>
    <div v-else-if="isStandard" class="standard-header-section">
      <TheHeader
        :header-title="standardHeaderTitle"
        :click-back-button-event="clickBackButtonEvent"
        text-align="left"
      />
    </div>
    <TheMyticketHeader
      v-else
      :ticket-name="displayPassName(bookGroup, masterPass)"
      :click-back-button-event="clickBackButtonEvent"
    />

    <div :class="{ 'qr-slider-inner': isQrSliderPass }">
      <div :class="contentClass">
        <ThePassUpperStandard
          v-if="isStandard"
          :upper-colors="theTicketBackgroundColor"
          :book="book"
          :pass="pass"
          :master-book="masterBook"
          :master-pass="masterPass"
          :book-group="bookGroup"
        />
        <ThePassUpper
          v-else
          :is-valid-pass-and-centered-q-r="isValidPassAndCenteredQR"
          :is-centered-q-r="isCenteredQR"
          :upper-colors="theTicketBackgroundColor"
        />
      </div>

      <!-- ドットの線 -->
      <TheDividerLine v-if="shouldShowDividerLine" />
      <TheDividerLine v-else-if="!isStandard" overlay />

      <div
        :class="['the-pass-lower', { 'qr-slider-lower-wrap': isQrSliderPass }]"
      >
        <!-- デマンドの定期券は特別に予約するボタン -->
        <ThePassLowerDemand v-if="isDemandPass" :is-standard="isStandard" />

        <!-- パス使用済み -->
        <FaceEndMessage v-else-if="isExpiredPass" />

        <!-- パス未使用(有効化前) -->

        <ThePassLowerBeforeValid
          v-else-if="countDown === null"
          :is-available-period="isAvailablePeriod"
          :show-issue-external-coupon-notification="
            showIssueExternalCouponNotification
          "
          :is-standard="isStandard"
        />

        <!-- パス有効化後 -->
        <ThePassLowerAfterValid
          v-else-if="countDown !== null && countDown !== '00:00:00:00'"
          :count-down="countDown"
          :is-valid-pass-and-centered-q-r="isValidPassAndCenteredQR"
          :update-pass-for-qr-ticket-gate="updatePassForQrTicketGate"
          :is-offline-qr="isOfflineQR"
          :is-qr-slider-pass="isQrSliderPass"
          :qrcode-background-image-url="qrcodeBackgroundImageUrl"
        />
      </div>
    </div>

    <!-- Out of Using Time dialog -->
    <DialogPassOutOfUsingTimeWarning
      ref="DialogPassOutOfUsingTimeWarning"
      :ok-button-clicked="onPassOutOfUsingTimeConfirm"
    />

    <DialogCouponVersionWarning ref="DialogCouponVersionWarning" />
  </div>
</template>

<script>
import { onMounted, computed, watchEffect, ref } from "vue";
import { useStore } from "vuex";

import API from "@/API";

import myTicketsCommonService from "@/services/common/myTickets";
import nativeHandlerService from "@/services/nativeHandler";
import passService from "@/services/pass";

import ThePassUpperStandard from "../../Components/Organisms/MyTickets/Pass/ThePassUpperStandard.vue";

function convertVersionForCompare(ver) {
  const verNums = ver.split(".");
  return verNums[0] * 100 + verNums[1] * 10 + verNums[2];
}

export default {
  name: "TemplatePass",
  components: {
    ThePassUpperStandard,
  },
  setup() {
    const store = useStore();

    const masterBook = store.getters["ticketing/masterBook"];
    const book = computed(() => store.getters["ticketing/book"]);
    const bookGroup = computed(() => store.getters["ticketing/bookGroup"]);
    const pass = computed(() => store.getters["ticketing/pass"]);
    const masterPass = computed(() => store.getters["ticketing/masterPass"]);
    const isDemandPass = computed(() => {
      return (
        [332, 333, 337, 338, 372, 373].includes(masterPass.value.id) &&
        isAvailablePeriod.value
      );
    });

    const interval = ref(null);
    const intervalHideIssuedExternalCouponNotification = ref(null);
    const countDown = ref(null);
    const isAvailablePeriod = computed(() =>
      myTicketsCommonService.checkIsAvailablePeriod(
        book.value.available_started_at,
        book.value.available_limited_at
      )
    );

    // Passが使用中かどうか
    const isValidPass = computed(() => {
      return countDown.value !== null && countDown.value !== "00:00:00:00";
    });

    // Passが有効期限を過ぎているかどうか？基本的にはカウントダウン制御だが、終了ボタンタイプはbookのis_expiredがtrueになるのでそれで判別
    const isExpiredPass = computed(
      () => countDown.value === "00:00:00:00" || book.value.is_expired
    );

    const isQrSliderPass = computed(() =>
      passService.checkIsQrSliderPass({
        passDispType: masterPass.value?.content?.description_json?.PassDispType,
      })
    );

    // isCenteredQRプロパティを取得
    const isCenteredQR = computed(
      () =>
        masterPass.value?.content?.description_json?.IsCenteredQrcodeTicket ||
        false
    );

    // isOfflineQRプロパティを取得
    const isOfflineQR = computed(
      () => masterPass.value?.content?.description_json?.IsOfflineQr || false
    );

    const qrcodeBackgroundImageUrl = computed(
      () =>
        masterPass.value?.content?.description_json?.QrcodeBackgroundImageUrl
    );


    const ticketColorFalldownColor = "#cccccc";
    const ticketColorFalldonwFontColor = "#000000";
    const ticketFontColor = "#FFFFFF";

    // チケット色（バリデーション付き）
    const theTicketBackgroundColor = computed(() => {
      const ticketColor =
        masterPass.value?.content?.description_json?.TicketColor;
      return passService.isValidTicketColor(ticketColor)
        ? { backgroundColor: ticketColor, fontColor: ticketFontColor }
        : {
            backgroundColor: ticketColorFalldownColor,
            fontColor: ticketColorFalldonwFontColor,
          };
    });

    // パスが有効かつisCenteredQRかどうか
    const isValidPassAndCenteredQR = computed(() => {
      return isValidPass.value && isCenteredQR.value;
    });

    const isStandard = computed(() => {
      const passInfoType =
        masterPass.value?.content?.description_json?.PassInfoType;
      return passInfoType === "Standard";
    });

    const shouldShowDividerLine = computed(() =>
      passService.checkShouldShowDividerLine({
        isCenteredQR: isCenteredQR.value,
        isStandard: isStandard.value,
        isValidPass: isValidPass.value,
      })
    );

    const contentClass = computed(() => {
      if (isValidPassAndCenteredQR.value) return "centered-qr-content";
      if (isStandard.value) return "";
      return "content";
    });

    const standardHeaderTitle = computed(() => {
      if (isExpiredPass.value) return "利用済みのチケット";
      if (isValidPass.value) return "利用中のチケット";
      return "利用開始前のチケット";
    });

    // QR改札チケットかどうか
    const isQrTicketGate = computed(() =>
      passService.checkIsQrTicketGate(pass.value)
    );
    const DialogPassOutOfUsingTimeWarning = ref(null);
    const ThePassIssuedExternalCouponNotification = ref(null);
    const DialogCouponVersionWarning = ref(null);

    const isNotTargetVersion = ref(false);
    const appVersion = computed(() => store.getters["common/appVersion"]);
    const device = computed(() => store.getters["common/device"]);

    onMounted(() => {
      updateCountDown();

      if (checkIsInUsingTime()) {
        setInterval(() => {
          if (!checkIsInUsingTime()) {
            window.location.reload();
          }
        }, 1 * 1000);
      } else if (checkIsBeforeEndTime()) {
        showPassOutOfUsingTimeDiaglog();
        return;
      }

      interval.value = setInterval(() => {
        updateCountDown();
      }, 1 * 1000);
    });

    const clickBackButtonEvent = async () => {
      // QR改札チケットで利用中の場合、MJとオムロンの同期のため、利用中の場合は閉じる際にgetPassをコールする
      if (!isExpiredPass.value && pass.value.is_valid && isQrTicketGate.value) {
        await API.getPass(pass.value.id);
      }
      // 画面輝度を元に戻す
      revertBrightnessOfScreen();
      nativeHandlerService.returnNativeAppMyticketPage();
    };

    // QR/QR改札チケットは、表示時に画面輝度を上げているので、期限切れ時や券面クローズ時に元に戻すための処理
    const revertBrightnessOfScreen = () => {
      if (
        isQrTicketGate.value ||
        passService.checkIsQrPass(masterPass.value) ||
        passService.checkIsOldQrPass(masterPass.value)
      ) {
        nativeHandlerService.ncRevertBrightness();
      }
    };

    // QR改札チケット用に、パス情報を再度取得してステータスを更新する処理
    const updatePassForQrTicketGate = async (timeout = 60 * 1000) => {
      await API.getPass(pass.value.id, timeout);

      // passが期限切れ(改札出場後)の場合、countDownを0にして使用済みとして扱う
      if (pass.value.is_expired) {
        countDown.value = "00:00:00:00";
      }
    };

    const updateCountDown = () => {
      // pass.user_limited_atが入ってから=pass有効化後に、countDownに値が入る
      if (pass.value.user_limited_at) {
        countDown.value = myTicketsCommonService.getCountDown(
          pass.value.user_limited_at
        );
      }
    };

    const showPassOutOfUsingTimeDiaglog = () => {
      console.log(
        "pass.using_started_at :>> ",
        pass.value.using_window[0].using_started_at
      );
      console.log(
        "pass.using_limited_at :>> ",
        pass.value.using_window[0].using_limited_at
      );
      DialogPassOutOfUsingTimeWarning.value.openDialog(pass.value);
    };

    const showIssueExternalCouponNotification = (isSuccess) => {
      ThePassIssuedExternalCouponNotification.value.showNotification(isSuccess);

      //reset 10s for retry action
      if (intervalHideIssuedExternalCouponNotification.value != null) {
        clearTimeout(intervalHideIssuedExternalCouponNotification.value);
      }

      intervalHideIssuedExternalCouponNotification.value = setTimeout(() => {
        ThePassIssuedExternalCouponNotification.value.hideNotification();
      }, 10 * 1000);
    };

    const retryIssuedExternalCoupon = async () => {
      try {
        const numberCouponIssued = await API.retryIssuedExternalCoupon(
          masterBook.id,
          book.value.id
        );
        if (numberCouponIssued >= 0) {
          showIssueExternalCouponNotification(true);
        }
      } catch (err) {
        showIssueExternalCouponNotification(false);
        console.log("retryIssuedExternalCoupon :>> issue coupon fail");
      }
    };

    const onPassOutOfUsingTimeConfirm = () => {
      nativeHandlerService.returnNativeAppMyticketPage();
    };

    const checkIsBeforeEndTime = () =>
      myTicketsCommonService.checkIsBeforeEndTime(book.value);

    const checkIsInUsingTime = () =>
      myTicketsCommonService.checkPassIsAvailablePeriod(pass.value);

    const openCouponVersionWarning = () => {
      DialogCouponVersionWarning.value.openDialog();
    };

    // 対象のバージョンかどうか確認
    const checkVersion = () => {
      let bool = false;
      switch (device.value) {
        case "IOS":
        case "ANDROID":
          bool =
            convertVersionForCompare(appVersion.value) <
            convertVersionForCompare("2.7.1");
          break;
        default:
          bool = false;
          break;
      }
      isNotTargetVersion.value = bool;
    };

    watchEffect(() => {
      // 期限切れになった場合、以降のステータスの変動はないのでカウントダウン計算を止める
      // また、画面輝度を元に戻す
      if (isExpiredPass.value) {
        clearInterval(interval.value);
        revertBrightnessOfScreen();
      }

      if (appVersion.value != null && appVersion.value != undefined) {
        checkVersion();
      }
    });

    return {
      countDown,
      isAvailablePeriod,
      isExpiredPass,
      isCenteredQR,
      isValidPass,
      isQrSliderPass,
      isValidPassAndCenteredQR,
      isStandard,
      shouldShowDividerLine,
      contentClass,
      standardHeaderTitle,
      book,
      bookGroup,
      pass,
      masterBook,
      masterPass,
      isDemandPass,
      clickBackButtonEvent,
      updatePassForQrTicketGate,
      showPassOutOfUsingTimeDiaglog,
      onPassOutOfUsingTimeConfirm,
      checkIsInUsingTime,
      checkIsBeforeEndTime,
      DialogPassOutOfUsingTimeWarning,
      showIssueExternalCouponNotification,
      retryIssuedExternalCoupon,
      isNotTargetVersion,
      openCouponVersionWarning,
      ThePassIssuedExternalCouponNotification,
      DialogCouponVersionWarning,
      theTicketBackgroundColor,
      isOfflineQR,
      qrcodeBackgroundImageUrl,
    };
  },
};
</script>

<style lang="scss" scoped>
$header-height: 107px;

.centered-qr-template-pass {
  display: flex;
  flex-direction: column;
  height: calc(100vh - #{$header-height});
  margin-top: $header-height;
}

.standard-template-pass {
  margin-top: $header-height;
  overflow-y: auto;
  height: calc(100vh - #{$header-height});
}

.standard-header-section,
.centered-qr-header-section {
  background-color: white;
  height: $header-height;
  width: 100%;
  border-bottom: 1px solid #e0e0e0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
}

.main-content {
  display: flex;
  flex-direction: column;
  background-color: grey;
  flex: 1;
  min-height: 0;
}

.centered-qr-main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.qr-slider-inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.qr-slider-lower-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
</style>
